"use client";

import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PlayCircle, Eye, EyeOff, Mic, Play, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { TextDiffHighlight } from "@/lib/text-diff-highlight-improved";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// 🎯 安全提取字符串值（处理 translation 可能是对象的情况）
function extractStringValue(value: string | Record<string, string> | undefined, preferredLang?: string): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const lang = preferredLang || 'zh';
    return value[lang] || value['zh'] || value['ja'] || value['en'] || Object.values(value)[0] || '';
  }
  return '';
}

interface ShadowingPracticeCardProps {
  sentence: {
    text: string;
    translation?: string | Record<string, string>;
    timestamp: [number, number];
  };
  showSentence: boolean;
  showTranslation: boolean;
  showTranscription: boolean;
  transcriptionAccuracy: number | null;
  transcribedText: string | null;
  transcriptionError: string | null;
  isRecording: boolean;
  isTranscribing: boolean;
  audioLevel: number;
  playbackSpeed: number;
  onToggleSentence: () => void;
  onToggleTranslation: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onReplay: (speed?: number) => void;
  onContinue: () => void;
  onSkip?: (e?: React.MouseEvent) => void;
  onWordClick?: (word: string) => void;
  containerClassName?: string;
  cardClassName?: string;
  disableEntranceAnimation?: boolean;
}

export const ShadowingPracticeCard: React.FC<ShadowingPracticeCardProps> = ({
  sentence,
  showSentence,
  showTranslation,
  showTranscription,
  transcriptionAccuracy,
  transcribedText,
  isRecording,
  isTranscribing,
  transcriptionError,
  audioLevel,
  playbackSpeed,
  onToggleSentence,
  onToggleTranslation,
  onStartRecording,
  onStopRecording,
  onReplay,
  onContinue,
  onSkip,
  onWordClick,
  containerClassName,
  cardClassName,
  disableEntranceAnimation = false,
}) => {
  const { t } = useI18n();
  
  // Internal state for playback speed (default 1x, or use playbackSpeed prop)
  const [currentSpeed, setCurrentSpeed] = useState(playbackSpeed || 1);
  
  // Speed options to cycle through
  const speedOptions = [0.75, 1, 1.25, 1.5, 2];
  
  const handleSpeedToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = speedOptions.findIndex(speed => Math.abs(speed - currentSpeed) < 0.01);
    const nextIndex = (currentIndex + 1) % speedOptions.length;
    setCurrentSpeed(speedOptions[nextIndex]);
  };

  const handleWordClick = async (word: string) => {
    if (onWordClick) {
      onWordClick(word);
    } else {
      try {
        const { doSpeak } = await import('@/components/tts');
        const controller = new AbortController();
        await doSpeak({
          provider: 'EdgeTTS',
          text: word.trim(),
          lang: 'en',
          rate: 10,
          volume: 100,
          signal: controller.signal,
        });
      } catch (error) {
        console.error('Failed to speak word:', word.trim(), error);
      }
    }
  };

  return (
    <div 
      className={cn("relative w-full bg-muted", containerClassName)}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <motion.div
        initial={
          disableEntranceAnimation ? false : { opacity: 0, scale: 0.96, y: 12 }
        }
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={
          disableEntranceAnimation ? undefined : { opacity: 0, scale: 0.96, y: 12 }
        }
        transition={{ duration: 0.25, ease: [0.26, 0.08, 0.25, 1] }}
        className={cn(
          "pointer-events-auto relative rounded-[40px] bg-white backdrop-blur-sm shadow-2xl transition-all duration-200 border border-gray-200/50 dark:border-white/20 dark:bg-slate-900/95",
          cardClassName
        )}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 pt-3">
          <div className="flex-1">
            <AnimatePresence initial={false}>
              <motion.span
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className="rounded-full bg-gray-100 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-gray-600 dark:bg-white/10 dark:text-white/60"
              >
                {t('shadowing.title', 'Shadowing Practice')}
              </motion.span>
            </AnimatePresence>
          </div>
          {onSkip && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onSkip(e);
              }}
              className="rounded-xl text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 focus:bg-gray-100 dark:text-white/80 dark:hover:bg-transparent dark:hover:text-white/80 dark:active:bg-transparent dark:focus:bg-transparent"
            >
              {t("common.close", "Close")}
            </Button>
          )}
        </div>

        <div className="px-4 pb-5 text-center text-gray-900 dark:text-white">
          {/* Sentence display */}
          <div className="mt-2 min-h-[60px] flex items-center justify-center">
            {showSentence ? (
              <div className="flex-1 text-left">
                <div className="text-[16px] font-semibold text-gray-900 dark:text-white">
                  {sentence.text.split(/(\s+|[^\w\s])/).map((token: string, index: number) => {
                    const isWord = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/.test(token);

                    if (isWord) {
                      return (
                        <span
                          key={index}
                          className="cursor-pointer text-gray-900 hover:text-gray-700 hover:bg-gray-100 px-1 py-0.5 rounded transition-colors dark:text-white dark:hover:text-white/80 dark:hover:bg-white/20"
                          onClick={() => handleWordClick(token)}
                          title={`Click to pronounce: ${token}`}
                        >
                          {token}
                        </span>
                      );
                    } else {
                      return <span key={index}>{token}</span>;
                    }
                  })}
                </div>
              </div>
            ) : (
              <div className="flex-1 text-sm font-light text-gray-500 italic text-center dark:text-white/60">
                {t('shadowing.showSentenceHint', 'Click the eye icon to show sentence')}
              </div>
            )}
            
            <div className="ml-0 flex flex-col items-end gap-2 flex-shrink-0">
              <div className="cursor-pointer p-1.5 hover:bg-gray-100 rounded-lg transition-colors dark:hover:bg-white/10">
                <SpeakerIcon
                  lang="en"
                  text={sentence.text}
                />
              </div>
              <button
                onClick={onToggleSentence}
                className="cursor-pointer p-1.5 hover:bg-gray-100 rounded-lg transition-colors dark:hover:bg-white/10"
                aria-label={showSentence ? t('shadowing.hideSentence', 'Hide sentence') : t('shadowing.showSentence', 'Show sentence')}
              >
                {showSentence ? (
                  <EyeOff className="w-5 h-5 text-gray-700 dark:text-white/80" />
                ) : (
                  <Eye className="w-5 h-5 text-gray-700 dark:text-white/80" />
                )}
              </button>
            </div>
          </div>

          {/* Translation */}
          {sentence.translation && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/20">
              {showTranslation ? (
                <div className="text-sm leading-relaxed text-gray-700 dark:text-white/85 text-left">
                  {extractStringValue(sentence.translation)}
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleTranslation}
                  className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 text-xs dark:text-white/60 dark:hover:text-white dark:hover:bg-white/10"
                >
                  {t('shadowing.showTranslation', 'Show Translation')}
                </Button>
              )}
            </div>
          )}

          {/* Transcription result */}
          {showTranscription && transcribedText && (
            <div className="mt-4 w-full">
              <TextDiffHighlight
                originalText={sentence.text}
                userTranscription={transcribedText}
                accuracy={transcriptionAccuracy || 0}
              />
            </div>
          )}

          {/* Error message */}
          {transcriptionError && (
            <div className="mt-4 w-full p-3 bg-red-50 rounded-lg border border-red-200 dark:bg-red-500/20 dark:border-red-500/30">
              <p className="text-sm text-red-700 dark:text-red-200">
                {t('shadowing.transcribeFailed', 'Failed to transcribe')}: {transcriptionError}
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
            {/* Playback speed toggle button */}
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleSpeedToggle}
              className="inline-flex h-8 items-center gap-1 rounded-full border border-white/60 bg-white/80 px-3 text-xs font-normal text-black shadow-sm hover:-translate-y-0.5 hover:bg-white"
            >
              <PlayCircle className="h-3 w-3 text-black" />
              <span>{currentSpeed.toFixed(2).replace(/\.00$/, "")}x</span>
            </Button>

            {/* Replay button */}
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReplay(currentSpeed);
              }}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/60 bg-white/80 px-3 text-xs font-normal text-black shadow-sm hover:-translate-y-0.5 hover:bg-white"
            >
              <RotateCcw className="h-3.5 w-3.5 text-black" />
              <span>{t('shadowing.replay', 'Replay')}</span>
            </Button>

            {/* Continue button */}
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onContinue();
              }}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/60 bg-white/80 px-3 text-xs font-normal text-black shadow-sm hover:-translate-y-0.5 hover:bg-white"
            >
              <Play className="h-3.5 w-3.5 text-black" />
              <span>{t('shadowing.continue', 'Continue')}</span>
            </Button>

            {/* Recording button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isRecording) {
                        onStopRecording();
                      } else {
                        onStartRecording();
                      }
                    }}
                    disabled={isTranscribing}
                    aria-label={isRecording ? t('shadowing.stopRecording', 'Stop Recording') : t('shadowing.startShadowing', 'Start Shadowing')}
                    aria-pressed={isRecording}
                    aria-busy={isTranscribing}
                    className={cn(
                      "flex size-10 items-center justify-center rounded-xl transition-colors",
                      "border border-gray-300 bg-white hover:bg-gray-50 dark:border-white/60 dark:bg-white/80 dark:hover:bg-white/90",
                      isTranscribing && "opacity-50 cursor-not-allowed",
                      transcriptionError && "bg-red-50 border-red-300 dark:bg-red-500/20 dark:border-red-500/30"
                    )}
                    style={{
                      boxShadow: isRecording
                        ? `0 0 0 ${Math.max(0, Math.min(4, audioLevel * 6))}px rgba(255, 255, 255, ${Math.max(0.05, Math.min(0.4, audioLevel * 0.4))})`
                        : 'none',
                    }}
                  >
                    {!isRecording && !isTranscribing && !transcriptionError && (
                      <Mic className="w-5 h-5 text-gray-700 dark:text-black" />
                    )}

                    {isRecording && (
                      <div className="w-5 h-5 rounded-full bg-red-500" />
                    )}

                    {isTranscribing && (
                      <svg
                        className="w-5 h-5 text-gray-700 dark:text-black animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2v4" />
                        <path d="m16.2 7.8 2.9-2.9" />
                        <path d="M18 12h4" />
                        <path d="m16.2 16.2 2.9 2.9" />
                        <path d="M12 18v4" />
                        <path d="m4.9 19.1 2.9-2.9" />
                        <path d="M2 12h4" />
                        <path d="m4.9 4.9 2.9 2.9" />
                      </svg>
                    )}

                    {transcriptionError && !isRecording && !isTranscribing && (
                      <svg
                        className="w-5 h-5 text-red-600 dark:text-red-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" x2="12" y1="4" y2="14" />
                        <line x1="12" x2="12.01" y1="20" y2="20" />
                      </svg>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isRecording ? t('shadowing.stopRecording', 'Stop Recording') : t('shadowing.startShadowing', 'Start Shadowing')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

